'use client';

import { QrCodeImage } from '@/app/components';
import { Button } from '@/components/ui';
import { logo } from '@/images';
import { cn } from '@/lib';
import { format } from 'date-fns';
import {
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  MailOpen,
  Wallet,
} from 'lucide-react';
import Image from 'next/image';
import { useRef, useState } from 'react';
// import { useSelector } from 'react-redux';
import { replaceImage } from '@/utils';
import { useReactToPrint } from 'react-to-print';
import { toJpeg } from 'html-to-image';

const canEmbedImage = async (img: HTMLImageElement) => {
  if (!img.complete || !img.naturalWidth) return false;
  try {
    const res = await fetch(img.currentSrc || img.src, { cache: 'force-cache' });
    return res.ok;
  } catch {
    return false;
  }
};

const isMobileDevice = () =>
  typeof navigator !== 'undefined' &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

// In-app browsers (WhatsApp, Instagram, Facebook, etc.) block both
// window.print() and blob downloads.
const isInAppBrowser = () =>
  typeof navigator !== 'undefined' &&
  /FBAN|FBAV|Instagram|WhatsApp|Line\/|Snapchat|Twitter|LinkedInApp|; wv\)/i.test(
    navigator.userAgent,
  );

// In-app browsers can't save files, so open the public ticket page in the
// phone's main browser, where the Download button works.
const openInMainBrowser = (bookingId: string) => {
  const { host, origin } = window.location;
  const path = `/ticket/${encodeURIComponent(bookingId)}`;
  if (/Android/i.test(navigator.userAgent)) {
    window.location.href = `intent://${host}${path}#Intent;scheme=https;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(origin + path)};end`;
  } else {
    window.location.href = `x-safari-${origin}${path}`;
  }
};

interface TProps {
  bookingId: string;
  eventName: string;
  bookingDateString: string;
  eventTime: string;
  eventTicketType: string;
  venueAddress: string;
  posterUrl: string;
  qrText: string;
  userName: string;
  totalAmount: string;
  bookedAt: string;
  numberOfSeats: number;
  ticketTypeArray: Array<{
    name: string;
    code: string;
    quantity: number;
  }>;
  className?: string;
  encQrObjectText: string;
  onspot?: boolean;
  transactionId:
    | {
        paymentBody: any;
      }
    | string;
  taxAmount: string;
  bookingAmount: string;
  convenienceAmount: string;
  donationAmount: string;
  specialDiscountAmount: string;
  paymentAmount: string;
  baseTicketAmount: string;
  bookingAmountBeforeGST: string;
  showCode?: boolean;
  slotLabel: string;
}

const Ticket = ({
  bookingId,
  eventName,
  bookingDateString: eventDate,
  eventTicketType,
  numberOfSeats,
  eventTime,
  venueAddress,
  posterUrl,
  userName,
  bookedAt,
  ticketTypeArray,
  className,
  encQrObjectText,
  onspot,
  transactionId,
  taxAmount,
  bookingAmount,
  convenienceAmount,
  donationAmount,
  specialDiscountAmount,
  paymentAmount,
  baseTicketAmount,
  bookingAmountBeforeGST,
  showCode = false,
  slotLabel,
}: TProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  // Inner content (not the scroll container) so the image isn't cut off at
  // the visible height.
  const captureRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // Hide the poster entirely if it is missing or fails to load.
  const [posterFailed, setPosterFailed] = useState(false);
  const showPoster = Boolean(posterUrl) && !posterFailed;

  const fileName = `ticket-${bookingId || 'fydo'}.pdf`;

  const createTicketImage = async () => {
    const node = captureRef.current;
    if (!node) throw new Error('Ticket not found');
    // Wait for the QR code / logo images to finish loading.
    await Promise.all(
      Array.from(node.querySelectorAll('img')).map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            }),
      ),
    );
    // Hide the poster / download button in the real DOM (not via `filter`),
    // because html-to-image copies computed heights and would otherwise
    // leave an empty gap where they were.
    const excluded = Array.from(
      node.querySelectorAll<HTMLElement>('[data-capture-exclude]'),
    );
    // Keep the poster only if it really rendered and can be embedded,
    // otherwise drop it so the ticket has no empty box.
    const poster = node.querySelector<HTMLImageElement>('[data-ticket-poster]');
    if (poster && !(await canEmbedImage(poster))) excluded.push(poster);
    excluded.forEach((el) => (el.style.display = 'none'));
    try {
      // html-to-image sizes the canvas from clientHeight by default, which
      // crops anything outside the visible area, so pass the full size.
      const options = {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        // Poster and logo are both served from /_next/image?url=..., so the
        // query string must be part of the cache key, otherwise the logo is
        // replaced by the poster.
        includeQueryParams: true,
        width: node.scrollWidth,
        height: node.scrollHeight,
        style: { overflow: 'visible', height: 'auto', maxHeight: 'none' },
      };
      // Safari often skips images on the first render, so render twice.
      await toJpeg(node, options).catch(() => null);
      const dataUrl = await toJpeg(node, { ...options, quality: 0.95 });
      return { dataUrl, width: options.width, height: options.height };
    } finally {
      excluded.forEach((el) => (el.style.display = ''));
    }
  };

  // Put the whole ticket on a single A4 page, scaled to fit and centred.
  const createTicketPdf = async () => {
    const [{ jsPDF }, image] = await Promise.all([
      import('jspdf'),
      createTicketImage(),
    ]);
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const scale = Math.min(
      (pageWidth - margin * 2) / image.width,
      (pageHeight - margin * 2) / image.height,
    );
    const width = image.width * scale;
    const height = image.height * scale;
    pdf.addImage(
      image.dataUrl,
      'JPEG',
      (pageWidth - width) / 2,
      margin,
      width,
      height,
    );
    return new File([pdf.output('blob')], fileName, {
      type: 'application/pdf',
    });
  };

  const saveFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadAsPdf = async () => {
    setIsDownloading(true);
    try {
      saveFile(await createTicketPdf());
    } catch {
      printTicket();
    } finally {
      setIsDownloading(false);
    }
  };

  const printTicket = useReactToPrint({
    contentRef,
    pageStyle: '@page { size: A4; margin: 10mm; }',
    onBeforePrint: async () => setIsDownloading(true),
    onAfterPrint: () => setIsDownloading(false),
    onPrintError: () => {
      setIsDownloading(false);
    },
  });

  const downloadTicket = () => {
    if (isMobileDevice() && isInAppBrowser() && bookingId) {
      openInMainBrowser(bookingId);
      // If the main browser didn't open (e.g. older iOS), try here anyway.
      setTimeout(() => {
        if (document.visibilityState === 'visible') downloadAsPdf();
      }, 1500);
    } else downloadAsPdf();
  };

  const ticketTypes = Array.isArray(ticketTypeArray) ? ticketTypeArray : [];
  const [isPaymentExpanded, setIsPaymentExpanded] = useState(false);
  const [isBookingChargesExpanded, setIsBookingChargesExpanded] =
    useState(true);

  const ticketAmount =
    Number(baseTicketAmount) ||
    Number(transactionId?.paymentBody?.originalAmount) ||
    0;
  const taxes =
    Number(taxAmount) || Number(transactionId?.paymentBody?.taxAmount) || 0;
  const bookingfee =
    Number(bookingAmount) ||
    Number(transactionId?.paymentBody?.bookingAmount || 0);
  const convenienceFee =
    Number(convenienceAmount) ||
    Number(transactionId?.paymentBody?.convenienceAmount) ||
    0;
  const donation =
    Number(donationAmount) ||
    Number(transactionId?.paymentBody?.donationAmount) ||
    0;
  const discount =
    Number(specialDiscountAmount) ||
    Number(transactionId?.paymentBody?.specialDiscountAmount) ||
    0;
  const total =
    Number(paymentAmount) ||
    Number(transactionId?.paymentBody?.paymentAmount) ||
    0;
  const baseBooking =
    Number(bookingAmountBeforeGST) ||
    Number(
      transactionId?.paymentBody?.bookingAmountBeforeGST ||
        Number(bookingAmount),
    ) ||
    0;
  const gst = Number(
    (Number(bookingAmountBeforeGST) > 0
      ? Number(bookingAmount) - Number(bookingAmountBeforeGST)
      : Number(transactionId?.paymentBody?.bookingAmountBeforeGST) > 0
        ? Number(transactionId?.paymentBody?.bookingAmount) -
          Number(transactionId?.paymentBody?.bookingAmountBeforeGST)
        : 0
    )?.toFixed(2),
  );

  return (
    <div
      className={cn(
        'hide-scrollbar flex w-full flex-col gap-3 overflow-scroll',
        className,
      )}
      ref={contentRef}
    >
      <div className='w-full print:px-2' ref={captureRef}>
        <div className='flex flex-col justify-between gap-4 p-3 px-6 md:flex-row'>
          <div className='flex min-w-0 flex-1 flex-col items-start gap-2 print:pt-8'>
            {bookingId && showCode && (
              <p className='rounded-lg border-2 border-dashed p-2 px-3 text-center text-lg font-bold'>
                {bookingId}
              </p>
            )}
            <p className='text-lg font-bold'>{eventName}</p>
            <p className='text-xs font-semibold text-zinc-600'>
              {venueAddress}
            </p>
            <div className='flex flex-wrap gap-2' data-capture-exclude='true'>
              <Button
                className='flex items-center gap-1 rounded-md border p-2 text-sm font-semibold print:hidden'
                variant='outline'
                onClick={downloadTicket}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : (
                  <Download size={16} />
                )}
                {isDownloading ? 'Downloading' : 'Download Ticket'}
              </Button>
            </div>
          </div>
          {showPoster && (
            <Image
              data-ticket-poster='true'
              src={replaceImage(posterUrl)}
              alt={`event ${bookingId}`}
              // Phone: full width under the title. Tablet / laptop / desktop:
              // beside the details, growing with the screen. Never cropped.
              className='h-auto max-h-52 w-full shrink-0 rounded-lg object-contain md:max-h-36 md:w-48 lg:max-h-40 lg:w-56 xl:max-h-44 xl:w-64'
              width={1080}
              height={1080}
              sizes='100vw'
              quality={90}
              onError={() => setPosterFailed(true)}
            />
          )}
        </div>

        <div className='flex flex-col items-center justify-center gap-4 p-3 px-6 md:flex-row'>
          {encQrObjectText && (
            <QrCodeImage
              value={encQrObjectText}
              size={300}
              className='w-[135px]'
            />
          )}

          <div className='w-full overflow-hidden rounded-lg border'>
            <div className='flex items-center justify-between border-b bg-gray-50 p-3'>
              <div>
                <p className='text-xs font-semibold tracking-wide text-zinc-700'>
                  <strong>{numberOfSeats}</strong> Ticket
                </p>
                <p className='text-primary font-bold uppercase'>
                  {ticketTypes
                    .map((ticketType) =>
                      typeof ticketType === 'object'
                        ? `${ticketType?.name?.trim()} : ${ticketType?.quantity}`
                        : ticketType,
                    )
                    .join(', ')}
                </p>
              </div>
              <div className='flex flex-row items-center justify-end gap-1'>
                <span className='rounded-sm bg-green-200 px-2 py-1 text-[10px] font-semibold tracking-wide text-green-600'>
                  {onspot ? 'Offline' : 'Online'}
                </span>
              </div>
            </div>
            <div className='grid grid-cols-2 p-3'>
              <div>
                <p className='text-xs font-semibold tracking-wide text-zinc-500'>
                  Date
                </p>
                <p className='text-base font-semibold'>{eventDate}</p>
              </div>
              <div>
                <p className='text-xs font-semibold tracking-wide text-zinc-500'>
                  Time
                </p>
                <p className='text-base font-semibold'>
                  {slotLabel || eventTime}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4 p-3 px-6 sm:grid-cols-3'>
          <div className='flex gap-2'>
            <MailOpen size={16} className='mt-1 shrink-0 text-zinc-500' />
            <div className='flex flex-col'>
              <p className='text-xs font-semibold text-zinc-500'>
                Delivery Name
              </p>
              <p className='text-base font-semibold text-zinc-700'>
                {userName}
              </p>
            </div>
          </div>
          {!onspot && (
            <div className='flex gap-2'>
              <Wallet size={16} className='mt-1 shrink-0 text-zinc-500' />
              <div className='flex flex-col'>
                <p className='text-xs font-semibold text-zinc-500'>Payment</p>
                <p className='text-base font-semibold text-zinc-700 uppercase'>
                  {eventTicketType === 'FREE_FCFS' ? 'free' : `₹ ${total}`}
                </p>
              </div>
            </div>
          )}
        </div>

        {!onspot && total > 0 && eventTicketType !== 'FREE_FCFS' && (
          <div className='px-6 py-2 pb-4'>
            <div className='overflow-hidden rounded-lg border border-dashed bg-zinc-50/50 transition-all'>
              <button
                onClick={() => setIsPaymentExpanded(!isPaymentExpanded)}
                className='flex w-full items-center justify-between p-4 text-left outline-none print:hidden'
                type='button'
              >
                <p className='text-sm font-semibold tracking-wider text-zinc-500'>
                  Payment Breakdown
                </p>
                {isPaymentExpanded ? (
                  <ChevronUp size={16} className='text-zinc-500' />
                ) : (
                  <ChevronDown size={16} className='text-zinc-500' />
                )}
              </button>
              <p className='hidden p-4 pb-2 text-sm font-semibold tracking-wider text-zinc-500 print:block'>
                Payment Breakdown
              </p>

              <div
                className={cn(
                  'flex flex-col gap-2 px-4 pb-4 text-sm text-zinc-600',
                  isPaymentExpanded ? 'block' : 'hidden',
                  'print:block',
                )}
              >
                <div className='flex justify-between'>
                  <span>Ticket Amount</span>
                  <span className='font-medium text-zinc-800'>
                    ₹ {ticketAmount}
                  </span>
                </div>
                {taxes > 0 && (
                  <div className='flex justify-between'>
                    <span>Taxes & GST</span>
                    <span className='font-medium text-zinc-800'>₹ {taxes}</span>
                  </div>
                )}

                {bookingfee > 0 && (
                  <button
                    className='w-full'
                    onClick={() => setIsBookingChargesExpanded((p) => !p)}
                  >
                    <div className='flex justify-between'>
                      <span className='flex items-center justify-center gap-1'>
                        Booking Fee (inc. of GST)
                        {isBookingChargesExpanded ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </span>
                      <span className='font-medium text-zinc-800'>
                        ₹ {bookingfee}
                      </span>
                    </div>

                    <div
                      className={cn(
                        'pt-1 text-xs',
                        isBookingChargesExpanded ? 'block' : 'hidden',
                      )}
                    >
                      <div className='flex justify-between'>
                        <span>Booking Fee</span>
                        <span className='font-medium text-zinc-800'>
                          ₹ {baseBooking}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>GST</span>
                        <span className='font-medium text-zinc-800'>
                          ₹ {gst}
                        </span>
                      </div>
                    </div>
                  </button>
                )}

                {convenienceFee > 0 && (
                  <div className='flex justify-between'>
                    <span>Convenience Fee</span>
                    <span className='font-medium text-zinc-800'>
                      ₹ {convenienceFee}
                    </span>
                  </div>
                )}
                {donation > 0 && (
                  <div className='flex justify-between'>
                    <span>Donation</span>
                    <span className='font-medium text-zinc-800'>
                      ₹ {donation}
                    </span>
                  </div>
                )}
                {discount > 0 && (
                  <div className='flex justify-between text-green-600'>
                    <span>Discount</span>
                    <span className='font-medium'>- ₹ {discount}</span>
                  </div>
                )}
                <div className='my-2 border-t border-dashed border-zinc-300' />
                <div className='flex justify-between text-base font-bold text-zinc-900'>
                  <span>Total Paid</span>
                  <span>₹ {total}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='border-t p-6'>
          <div className='flex flex-col items-center justify-between gap-2 md:flex-row'>
            <div className='flex flex-col'>
              <p className='text-center text-xs font-semibold text-zinc-500 md:text-left'>
                Purchased on
              </p>
              <p className='text-sm font-semibold text-zinc-700'>
                {format(bookedAt, 'dd LLL yyyy h:mm a')}
              </p>
            </div>
            <div>
              <Image
                alt='Greet Fydo Logo'
                aria-label='Greet Fydo Logo'
                className='mb-1 h-6 w-auto sm:h-8'
                src={logo}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
